// LiveStreamPopups.js
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRight } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import usePlaceStreams from '../../hooks/usePlaceStreams';
import { governmentName, liveStats, resultsOf, sortByScope } from '../../utils/streams';
import StreamPlayerModal from './StreamPlayerModal';

import './Streams.css';

/**
 * LiveStreamPopups
 *
 * The live strip at the top of the For You (Glance) feed: a horizontal row of
 * small cards, one per government streaming live right now for the reader's
 * place. Deliberately condensed -- two lines and a dot, no buttons:
 *
 *   line 1   alternates every 5 seconds between who is streaming ("Fort Worth
 *            city") and what ("City Council Meeting | September 29..."),
 *            cut to one line
 *   line 2   how long it has been live and how many are watching
 *   right    the pulsing accent dot that says it is live
 *
 * Every card flips on the same beat, so the row changes together rather than
 * flickering card by card. Tapping a card opens the player, which carries
 * every action the card leaves out: Save, Share, Watch on YouTube and Hide.
 *
 * Each card is at most three quarters of the column and never wider than
 * 280px, so it is always narrower than a news item and the next card peeks in
 * from the right. Up to `max` cards (3), then a View all tile that opens Live
 * streams (/us/live-streams), shown when the page passes `onSeeAll`.
 *
 * Nothing renders when nothing is live, so the feed is unchanged most of the
 * day. Continuous streams (12 hours or more, usually a camera) are left out
 * here -- they belong in Live streams, not above the news -- and so is
 * anything the reader hid this session.
 */

const DISMISS_KEY = 'liveStreamPopupsDismissed';
const DEFAULT_VISIBLE = 3;
const ROTATE_MS = 5000;

const readDismissed = () => {
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY);
    const ids = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(ids) ? ids : []);
  } catch {
    return new Set();
  }
};

const writeDismissed = (ids) => {
  try {
    sessionStorage.setItem(DISMISS_KEY, JSON.stringify([...ids]));
  } catch {
    // Private mode, or storage disabled. Hiding still works for this page.
  }
};

const LiveChip = ({ stream, showWho, onOpen }) => {
  const { t } = useTranslation('common');
  const who = governmentName(stream);
  // A stream with no government name has only its title to show.
  const firstLine = showWho && who ? who : stream.title;
  const stats = liveStats(stream, t);

  return (
    <button
      type="button"
      className="live-chip"
      onClick={() => onOpen(stream)}
      aria-label={[who, stream.title, stats].filter(Boolean).join(', ')}
    >
      <span className="live-chip-text">
        {/* Keyed on the line's content so each flip replays the fade. */}
        <span key={firstLine} className="live-chip-line live-chip-line--main">
          {firstLine}
        </span>
        {stats && <span className="live-chip-line live-chip-line--stats">{stats}</span>}
      </span>
      <span className="live-chip-dot" aria-hidden />
    </button>
  );
};

function LiveStreamPopups({ max = DEFAULT_VISIBLE, onSeeAll = null }) {
  const { t } = useTranslation('common');
  const { data } = usePlaceStreams();
  const [openStream, setOpenStream] = useState(null);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [showWho, setShowWho] = useState(true);

  const visible = useMemo(
    () =>
      sortByScope(resultsOf(data?.live).filter((row) => !row.continuous)).filter(
        (row) => !dismissed.has(row.video_id)
      ),
    [data, dismissed]
  );

  const hasCards = visible.length > 0;

  // One beat for the whole row. Runs only while there is something to flip.
  useEffect(() => {
    if (!hasCards) return undefined;
    const timer = setInterval(() => setShowWho((current) => !current), ROTATE_MS);
    return () => clearInterval(timer);
  }, [hasCards]);

  const dismiss = (videoId) => {
    setDismissed((current) => {
      const next = new Set(current);
      next.add(videoId);
      writeDismissed(next);
      return next;
    });
  };

  // The player stays mounted even if the stream drops off the live list, so
  // the reader is never cut off mid-watch.
  if (!hasCards && !openStream) return null;

  const shown = visible.slice(0, max);
  const hiddenCount = visible.length - shown.length;

  return (
    <>
      {hasCards && (
        <section className="live-popups" aria-label={t('common:liveNowLabel', 'Live now')}>
          {shown.map((stream) => (
            <LiveChip key={stream.video_id} stream={stream} showWho={showWho} onOpen={setOpenStream} />
          ))}

          {onSeeAll && (
            <button type="button" className="live-popups-view-all" onClick={onSeeAll}>
              <span className="live-popups-view-all-label">
                {hiddenCount > 0
                  ? t('common:viewAllMoreLive', 'View all ({{count}} more)', { count: hiddenCount })
                  : t('common:viewAllShort', 'View all')}
              </span>
              <ChevronRight size={14} aria-hidden />
            </button>
          )}
        </section>
      )}

      <StreamPlayerModal stream={openStream} onHide={() => setOpenStream(null)} onDismiss={dismiss} />
    </>
  );
}

export default LiveStreamPopups;